import {
  ChartPieIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  PhotoIcon,
  UserGroupIcon,
  HomeModernIcon,
} from '@heroicons/react/24/outline';
import type { RegistryEntry, ResolvedEntry, CapabilitiesView } from './types';

export type { RegistryEntry, ResolvedEntry, RegistrySlot, CapabilitiesView } from './types';

// Main navigation (P16 §4). The existing nav, re-expressed as registry entries:
// permission-gated (owners hold all → their nav is unchanged) with feature keys
// on the surfaces whose feature is trial-on today. Later features add a row here
// — the shell (Sidebar) never changes.
export const navRegistry: RegistryEntry[] = [
  { key: 'dashboard', slot: 'main', label: 'Dashboard', to: '/app', icon: ChartPieIcon, order: 10 },
  // The Sites registry is the CORE surface — permission-gated only (story
  // 2-20). It previously carried featureKey 'domains.building', a Pro-tier
  // domain-pack entitlement, while the page/route/actions/backend gate only on
  // subjects:* — so a free org saw a padlock teasing an upsell it already had.
  // Domain packs gate domain-specific behaviour (P03), not the base surface.
  //
  // "Sites" is a CONSTANT, not a vocabulary term (P03 §0/§5, story 3-2): the
  // global registry keeps this name under every domain pack, and only the
  // RECORDS inside it take the pack's noun (Building / Website / Machine /
  // Outdoor site) via useVocab(). Never resolve this label through vocab().
  // Story 3-1b moved the route to /app/sites (P03 §0); /app/properties is now
  // a state-preserving compatibility redirect (App.tsx) for one release. The
  // KEY stays 'properties' on purpose — it is an internal registry identifier,
  // not user-facing text, and renaming it would churn every consumer for no
  // behaviour change during a compatibility window whose whole premise is that
  // internal names keep saying "property" (P03 §6).
  { key: 'properties', slot: 'main', label: 'Sites', to: '/app/sites', icon: HomeModernIcon, permission: 'subjects:read', order: 20 },
  { key: 'inspections', slot: 'main', label: 'Inspections', to: '/app/items', icon: PhotoIcon, permission: 'inspections:read', featureKey: 'inspections.core', order: 30 },
  // 'reports' (order 40) moved into src/modules/reports's manifest (story
  // 4-9a, DOD §1.8 "registries, not shell edits"), the same migration story
  // 4-2a made for templates. The row keeps its exact key/label/permission/
  // featureKey/order — a module migration must be invisible to the flag
  // behaviour story-2-9 asserts on this row by name.
  // 'templates' (order 50) moved into src/modules/templates's manifest (story
  // 4-2a, DOD §1.8 "registries, not shell edits") — the templates LIBRARY page
  // is the surface that became a module; this static registry no longer
  // declares its row.
  { key: 'activity', slot: 'main', label: 'Activity', to: '/app/activity', icon: ClipboardDocumentListIcon, order: 60 },
  // 'billing' (order 70) is contributed by the billing module manifest
  // (src/modules/billing) via the host — the shell no longer hard-codes it.
  { key: 'settings', slot: 'main', label: 'Settings', to: '/app/settings', icon: Cog6ToothIcon, order: 80 },
  // Org governance lives on the Access console (1C-3); platform governance on
  // the owner-plane Platform console (1C-4, Sidebar-gated on platformRole).
  // The legacy superadmin rows ('admin-dashboard', 'admin-users',
  // 'admin-settings' → /app/admin*) dead-ended org users at /app and were
  // retired in story 2-8 — these two consoles are the only governance surfaces.
  { key: 'access', slot: 'main', label: 'Access & roles', to: '/app/settings/access', icon: UserGroupIcon, permission: 'org:members:manage', section: 'Administration', order: 90 },
];

// Settings / dashboard-tile / "+ New" / agent-suggestion registries: same shape,
// populated by their features as they land (per-wave). The shell slots exist and
// render whatever registers.
export const settingsRegistry: RegistryEntry[] = [
  // Pro-only feature with a real surface behind it (the Access-console role
  // editor, 1C-3/1G-6). On free/trial plans this resolves LOCKED → the row
  // renders with a lock affordance → 2-1 teaser → 2-2 request (story 2-6).
  // Gates on org:roles:read deliberately: org:roles:manage sits in the
  // feature's permissionMask, so it is stripped from the capability doc while
  // the feature is locked — gating on it would hide the very row that must
  // advertise the upsell.
  {
    key: 'custom-roles',
    slot: 'settings',
    label: 'Custom roles',
    description: 'Create custom roles with fine-grained permissions for your team.',
    to: '/app/settings/access',
    permission: 'org:roles:read',
    featureKey: 'org.customRoles',
    order: 10,
  },
  // Org-plane feature toggles (story 2-10, P02 §6): the superadmin flips their
  // own entitled flags. Permission-gated only — no featureKey, so the row can
  // never gate itself off.
  {
    key: 'org-features',
    slot: 'settings',
    label: 'Features',
    description: 'Turn the features your plan includes on or off for this organization.',
    to: '/app/settings/features',
    permission: 'org:flags:manage',
    order: 20,
  },
  // Story 6-8 (permission split added 4-105). Same shape as 'custom-roles'
  // above: a Pro-only feature with a real surface behind it. Gates on
  // org:branding:read deliberately: org:branding:manage sits in the
  // feature's permissionMask, so it is stripped from the capability doc
  // while the feature is locked — gating on it would hide the very row that
  // must advertise the upsell (this was 4-105's bug: gating directly on
  // org:branding:manage meant a locked-plan owner, including the org OWNER,
  // could never reach the teaser — the permission check failed before the
  // feature-state check ever ran). On free/trial this resolves LOCKED
  // (org.branding is Pro-entitled) → the row renders with a lock affordance
  // → the 2-1 teaser. On Pro, a principal without org:branding:read never
  // sees the row at all (permission check happens BEFORE the feature-state
  // check in `resolveEntries`) — matching the done-when's "a Pro user
  // WITHOUT that permission does not [get a working editor]"; the editor
  // itself still requires org:branding:manage (unchanged, write-gated) via
  // its own route/API checks.
  {
    key: 'org-branding',
    slot: 'settings',
    label: 'Branding',
    description: 'Apply your brand across reports and the portal.',
    to: '/app/settings/branding',
    permission: 'org:branding:read',
    featureKey: 'org.branding',
    order: 30,
  },
];
export const tileRegistry: RegistryEntry[] = [];
export const newMenuRegistry: RegistryEntry[] = [];
export const agentSuggestionRegistry: RegistryEntry[] = [];

// Render rule (P16 §4): an entry renders iff permission ∈ capabilities ∧ its
// feature flag is not hidden. 'on' → normal; 'locked' (unless discovery hidden)
// → shown with a lock affordance; 'off' or hidden → not rendered. Sorted by order.
export function resolveEntries(entries: RegistryEntry[], caps: CapabilitiesView): ResolvedEntry[] {
  return entries
    .filter((entry) => !entry.permission || caps.hasPermission(entry.permission))
    .map((entry) => ({ entry, fs: entry.featureKey ? caps.featureState(entry.featureKey) : { state: 'on' } }))
    .filter(({ fs }) => {
      if (fs.state === 'on') return true;
      if (fs.state === 'locked') return fs.discovery?.mode !== 'hidden';
      return false;
    })
    .map(({ entry, fs }) => ({ ...entry, locked: fs.state === 'locked' }))
    .sort((a, b) => a.order - b.order);
}

export interface Breadcrumb {
  label: string;
  to?: string;
  current: boolean;
}

// Breadcrumb trail (story 2-104): the SAME resolved entries that drive the
// sidebar's active state (Sidebar.tsx's `active` check is `pathname === to ||
// pathname.startsWith(to + '/')`) also drive the crumb trail — one registry,
// two renderings, so a route can never be "active" in the nav but absent from
// the crumb (or vice versa). Only entries a locked/permission-denied nav row
// would never show are excluded (resolveEntries already dropped them), so a
// crumb never names a destination the principal can't reach.
export function resolveBreadcrumbs(pathname: string, entries: RegistryEntry[], caps: CapabilitiesView): Breadcrumb[] {
  // The dashboard is the trail's own root — a single "Dashboard" crumb on the
  // dashboard page itself would just repeat the page's own heading.
  if (pathname === '/app') return [];
  const resolved = resolveEntries(entries, caps).filter((entry): entry is ResolvedEntry & { to: string } => !entry.locked && Boolean(entry.to));
  const matches = resolved
    // The dashboard-root crumb is already added unconditionally below — without
    // this exclusion, the dashboard nav entry's own `to: '/app'` ALSO satisfies
    // `pathname.startsWith(entry.to + '/')` for every /app/* route (it's a
    // prefix of all of them), producing a second, duplicate "Dashboard" crumb
    // ahead of the real trail. Found live (story 2-129) once TopBar started
    // actually rendering this function's output where a human/spec could see
    // it — pre-existing since 2-104, no prior spec asserted real crumb content.
    .filter((entry) => entry.to !== '/app')
    .filter((entry) => pathname === entry.to || pathname.startsWith(`${entry.to}/`))
    .sort((a, b) => a.to.length - b.to.length);
  if (matches.length === 0) return [];

  const trail: Breadcrumb[] = [];
  if (pathname !== '/app') trail.push({ label: 'Dashboard', to: '/app', current: false });
  matches.forEach((entry, index) => {
    const isLast = index === matches.length - 1;
    trail.push(isLast ? { label: entry.label, current: true } : { label: entry.label, to: entry.to, current: false });
  });
  return trail;
}
